import { FC } from "react";
import { Box, Paper, Skeleton, Stack, Tooltip, Typography } from "@mui/material";
import { formatNumber } from "../../../../../../../common/utils";
import { ChartNoDataOverlay } from "../../../../../../../components/charts/components/ChartNoDataOverlay";
import { MeasurableValueDTO } from "../../../../../../../datasources/api-client/data-contracts";

type Props = {
	title: string;
	type: string;
	baseline?: MeasurableValueDTO;
	comparison?: MeasurableValueDTO;
	height?: number;
	baselineLabel?: string;
	comparisonLabel?: string;
	isLoading?: boolean;
};

const ticks = [0, 20, 40, 60, 80, 100];

const chartPaddingX = 1;
const barHeight = 28;
const barGap = 4;
const noDataHeight = 160;

const BaselineComparisonBarChart: FC<Props> = ({
	title,
	type,
	baseline,
	comparison,
	height = 260,
	baselineLabel = "Baseline",
	comparisonLabel = "Comparison",
	isLoading,
}) => {
	const baselineValue = baseline?.value;
	const comparisonValue = comparison?.value;

	const hasNoData = baselineValue === undefined && comparisonValue === undefined;

	const safeBaselineValue = baselineValue ?? 0;
	const safeComparisonValue = comparisonValue ?? 0;

	// Formats percentage values
	const formatValue = (value: number): string => `${formatNumber(value, "decimal", 1, 1)}%`;

	if (isLoading) {
		return <Skeleton variant="rounded" height={200} width="100%" />;
	}

	return (
		<Paper elevation={0} sx={{ p: 2 }}>
			<Typography variant="h5" fontWeight={500}>
				{`${title} - ${type}`}
			</Typography>

			<Stack spacing={2} width="100%" minHeight={height} p={2}>
				{/* Chart area */}
				<Stack flex={1} position="relative" px={chartPaddingX} justifyContent="center">
					{/* Vertical grid lines */}
					{ticks.map((tick) => {
						const left = `${tick}%`;

						return (
							<Stack
								key={tick}
								position="absolute"
								left={left}
								top={0}
								bottom={0}
								width="1px"
								bgcolor="divider"
								sx={{
									transform: "translateX(-50%)",
									opacity: 0.75,
								}}
							/>
						);
					})}

					{/* Empty state */}
					{hasNoData && (
						<Box position="relative" width="100%" height={noDataHeight}>
							<ChartNoDataOverlay />
						</Box>
					)}

					{/* Bars */}
					{!hasNoData && (
						<Stack spacing={`${barGap}px`}>
							<Tooltip title={`${baselineLabel}: ${formatValue(safeBaselineValue)}`} arrow>
								<Stack
									height={barHeight}
									width={`${Math.min(safeBaselineValue, 100)}%`}
									borderRadius={1}
									bgcolor="primary.main"
								/>
							</Tooltip>

							<Tooltip title={`${comparisonLabel}: ${formatValue(safeComparisonValue)}`} arrow>
								<Stack
									height={barHeight}
									width={`${Math.min(safeComparisonValue, 100)}%`}
									borderRadius={1}
									bgcolor="secondary.main"
								/>
							</Tooltip>
						</Stack>
					)}
				</Stack>

				{/* X axis */}
				{!hasNoData && (
					<Stack position="relative" px={chartPaddingX} height={24}>
						{ticks.map((tick) => {
							const left = `${tick}%`;

							return (
								<Stack
									key={tick}
									position="absolute"
									left={left}
									sx={{ transform: "translateX(-50%)" }}
								>
									<Typography variant="caption" color="text.secondary" whiteSpace="nowrap">
										{tick.toFixed(1)}%
									</Typography>
								</Stack>
							);
						})}
					</Stack>
				)}

				{/* Legend */}
				{!hasNoData && (
					<Stack alignItems="center">
						<Stack direction="row" spacing={1} justifyContent="center">
							<Stack direction="row" spacing={1} alignItems="center">
								<Stack width={12} height={12} borderRadius={1} bgcolor="primary.main" />
								<Typography variant="caption" fontWeight={500}>
									{baselineLabel}
								</Typography>
							</Stack>

							<Stack direction="row" spacing={1} alignItems="center">
								<Stack width={12} height={12} borderRadius={1} bgcolor="secondary.main" />
								<Typography variant="caption" fontWeight={500}>
									{comparisonLabel}
								</Typography>
							</Stack>
						</Stack>
					</Stack>
				)}
			</Stack>
		</Paper>
	);
};

export default BaselineComparisonBarChart;
